import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton, TextField, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ContactInterface, TimelineNoteInterface } from '../types';
import { addTimelineNote, updateTimelineNote, deleteTimelineNote } from '../api/contacts';

interface TimelineNotesModalProps {
  open: boolean;
  onClose: () => void;
  contact: ContactInterface | null;
  onNotesUpdated: () => void;
}

function TimelineNotesModal({ open, onClose, contact, onNotesUpdated }: TimelineNotesModalProps): React.JSX.Element {
  const [addMode, setAddMode] = useState(false);
  const [editMode, setEditMode] = useState<string | null>(null);
  
  const [currentNote, setCurrentNote] = useState('');
  const [currentAuthor, setCurrentAuthor] = useState('');
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setCurrentNote('');
    setCurrentAuthor('');
    setAddMode(false);
    setEditMode(null);
  };

  const handleAddClick = () => {
    resetForm();
    setAddMode(true);
  };

  const handleEditClick = (note: TimelineNoteInterface) => {
    setCurrentNote(note.note);
    setCurrentAuthor(note.author || '');
    setEditMode(note.id);
  };

  const handleDelete = async (noteId: string) => {
    if (!contact || !window.confirm('האם אתה בטוח שברצונך למחוק הערה זו?')) return;
    setLoading(true);
    try {
      await deleteTimelineNote(contact.id, noteId);
      onNotesUpdated();
    } catch (error) {
      console.error('Failed to delete note', error);
      alert('שגיאה במחיקת הערה');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!contact || !currentNote.trim()) return;
    setLoading(true);
    try {
      if (editMode) {
        await updateTimelineNote(contact.id, editMode, {
          note: currentNote,
          author: currentAuthor.trim() || undefined
        });
      } else {
        await addTimelineNote(contact.id, {
          note: currentNote,
          author: currentAuthor.trim() || undefined
        });
      }
      onNotesUpdated();
      resetForm();
    } catch (error) {
      console.error('Failed to save note', error);
      alert('שגיאה בשמירת הערה');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    resetForm();
    onClose();
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${d.toLocaleDateString('he-IL')} ${d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <Dialog open={open} onClose={handleModalClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>הערות - {contact?.name}</span>
        {!addMode && !editMode && (
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleAddClick} disabled={loading}>
            הוסף הערה
          </Button>
        )}
      </DialogTitle>
      
      <DialogContent dividers>
        {(addMode || editMode) ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="רושם ההערה"
              value={currentAuthor}
              onChange={(e) => setCurrentAuthor(e.target.value)}
              placeholder="אופציונלי"
              fullWidth
              size="small"
            />
            <TextField
              label="הערה"
              value={currentNote}
              onChange={(e) => setCurrentNote(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(!contact?.timeline_notes || contact.timeline_notes.length === 0) ? (
              <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                אין הערות לשחיין זה.
              </Typography>
            ) : (
              contact.timeline_notes.map((note) => (
                <Box key={note.id} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatDate(note.created_at)}
                      </Typography>
                      {note.author && (
                        <Typography variant="subtitle2" color="primary">
                          {note.author}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => handleEditClick(note)} disabled={loading}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(note.id)} disabled={loading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {note.note}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        {(addMode || editMode) ? (
          <>
            <Button onClick={resetForm} disabled={loading}>ביטול</Button>
            <Button onClick={handleSave} variant="contained" color="primary" disabled={loading || !currentNote.trim()}>
              {loading ? <CircularProgress size={20} /> : 'שמור'}
            </Button>
          </>
        ) : (
          <Button onClick={handleModalClose}>סגור</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default TimelineNotesModal;
